// Saved cards for the signed-in shopper
// ────────────────────────────────────────────────────────────────────
// Cards live on the shopper's Customer on the FASH platform Stripe
// account, so every read and write goes through a callable. Nothing about
// a card is stored in Firestore.

import { useState, useEffect, useCallback, useRef } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../config/firebase';
import { useAuth } from './useAuth';
import type { SavedPaymentMethod, PaymentMethodsResponse } from '../types/payment';

export interface PaymentMethodsState {
  methods: SavedPaymentMethod[];
  defaultId: string | null;
  loading: boolean;
  /** True while a delete or set-default is in flight, keyed by method id. */
  pendingId: string | null;
  error: string | null;
  refresh: () => Promise<void>;
  remove: (paymentMethodId: string) => Promise<void>;
  makeDefault: (paymentMethodId: string) => Promise<void>;
}

type MutationResponse = PaymentMethodsResponse & { success: boolean };

export function usePaymentMethods(): PaymentMethodsState {
  const { user } = useAuth();
  const [methods, setMethods] = useState<SavedPaymentMethod[]>([]);
  const [defaultId, setDefaultId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [pendingId, setPendingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  const refresh = useCallback(async () => {
    if (!user) {
      setMethods([]);
      setDefaultId(null);
      setLoading(false);
      return;
    }

    setError(null);
    try {
      const call = httpsCallable<Record<string, never>, PaymentMethodsResponse>(
        functions,
        'listPaymentMethods',
      );
      const { data } = await call({});
      if (!mounted.current) return;
      setMethods(data.paymentMethods || []);
      setDefaultId(data.defaultPaymentMethodId || null);
    } catch (err: any) {
      if (!mounted.current) return;
      setError(err?.message || 'Could not load your payment methods.');
    } finally {
      if (mounted.current) setLoading(false);
    }
  }, [user]);

  useEffect(() => { void refresh(); }, [refresh]);

  // Both mutations return the refreshed list, so the UI settles in one
  // round trip instead of a write followed by a read.
  const mutate = useCallback(
    async (name: string, paymentMethodId: string) => {
      setPendingId(paymentMethodId);
      setError(null);
      try {
        const call = httpsCallable<{ paymentMethodId: string }, MutationResponse>(functions, name);
        const { data } = await call({ paymentMethodId });
        if (!mounted.current) return;
        setMethods(data.paymentMethods || []);
        setDefaultId(data.defaultPaymentMethodId || null);
      } catch (err: any) {
        if (mounted.current) setError(err?.message || 'Something went wrong. Please try again.');
        throw err;
      } finally {
        if (mounted.current) setPendingId(null);
      }
    },
    [],
  );

  const remove = useCallback(
    (paymentMethodId: string) => mutate('deletePaymentMethod', paymentMethodId),
    [mutate],
  );

  const makeDefault = useCallback(
    (paymentMethodId: string) => mutate('setDefaultPaymentMethod', paymentMethodId),
    [mutate],
  );

  return { methods, defaultId, loading, pendingId, error, refresh, remove, makeDefault };
}
